/**
 * Display ID Builder para Dashboard Widgets
 *
 * Construye labels para los items de widgets usando los campos
 * devueltos por el servidor y la configuración de displayId.
 */

/**
 * Configuración de un campo del displayId
 */
export interface DisplayIdFieldConfig {
  fieldName: string;
  multilang: boolean;
}

/**
 * Metadata devuelta por el servidor para widgets con displayId de entidades
 */
export interface DisplayIdMetadata {
  entityType?: string;
  displayIdFields?: DisplayIdFieldConfig[];
  languages?: string[];
  // Para workflow states
  isWorkflow?: boolean;
  stateLabelsKey?: string;
}

/**
 * Item de datos del widget con campos dinámicos
 */
export interface WidgetDataItem {
  groupKey: string;
  count: number;
  [key: string]: unknown;
}

/**
 * Construye el label de un item usando los campos del displayId.
 *
 * @param item - Item de datos con los campos del displayId
 * @param metadata - Configuración del displayId (entityType, fields, languages)
 * @param currentLang - Idioma actual del usuario (es, en, pt)
 * @returns El label construido para mostrar
 */
export function buildDisplayIdLabel(
  item: WidgetDataItem,
  metadata: DisplayIdMetadata | undefined,
  currentLang: string
): string {
  // Si no hay metadata o no hay campos, usar groupKey
  if (!metadata?.displayIdFields?.length) {
    return item.groupKey || '(Sin valor)';
  }

  const parts: string[] = [];

  for (const fieldConfig of metadata.displayIdFields) {
    let value: unknown;

    if (fieldConfig.multilang) {
      // Campo multilang: buscar con sufijo del idioma actual
      const fieldWithLang = `${fieldConfig.fieldName}_${currentLang}`;
      value = item[fieldWithLang];

      // Fallback a otros idiomas si no existe
      if ((value === undefined || value === null) && metadata.languages) {
        for (const lang of metadata.languages) {
          const fallbackField = `${fieldConfig.fieldName}_${lang}`;
          if (item[fallbackField] !== undefined && item[fallbackField] !== null) {
            value = item[fallbackField];
            break;
          }
        }
      }
    } else {
      // Campo normal
      value = item[fieldConfig.fieldName];
    }

    if (value !== undefined && value !== null && value !== '') {
      parts.push(String(value));
    }
  }

  // Si no hay partes, usar groupKey
  if (parts.length === 0) {
    return item.groupKey || '(Sin valor)';
  }

  // Concatenar partes con espacio (patrón típico de displayId)
  return parts.join(' ');
}

/**
 * Procesa los datos del widget para agregar groupLabel a cada item.
 *
 * @param data - Array de items del widget
 * @param metadata - Metadata con configuración de displayId
 * @param currentLang - Idioma actual
 * @returns Array con groupLabel agregado a cada item
 */
export function processWidgetDataWithLabels<T extends WidgetDataItem>(
  data: T[],
  metadata: DisplayIdMetadata | undefined,
  currentLang: string
): (T & { groupLabel: string })[] {
  return data.map((item) => ({
    ...item,
    groupLabel: buildDisplayIdLabel(item, metadata, currentLang),
  }));
}
